import { useRef, useCallback, useState } from "react";
import TrackRow from "./TrackRow";
import { PX_PER_SEC } from "./Keyframe";

const PROP_LABELS = {
  position: "Position",
  rotate: "Rotate",
  scale: "Scale",
  skew: "Skew",
};

export default function Timeline({
  selectedId,
  selectedLabel,
  keyframeData,
  duration,
  currentTime,
  isPlaying,
  loop,
  onToggleLoop,
  onSeek,
  onPlayPause,
  onDurationChange,
  onMoveKeyframe,
  onDeleteKeyframe,
  onEasingChange,
  onDragStart,
  onDragEnd,
}) {
  const rulerRef = useRef(null);
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);
  const width = Math.max(duration * PX_PER_SEC, 400);

  const propsForSelected = selectedId ? keyframeData[selectedId] || {} : {};
  const activeProps = Object.keys(PROP_LABELS).filter(
    (p) => propsForSelected[p]?.length
  );

  const timeFromClientX = useCallback((clientX) => {
    if (!rulerRef.current) return 0;
    const rect = rulerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    return Math.max(0, Math.min(x / PX_PER_SEC, duration));
  }, [duration]);

  // mousedown di MANA PUN di area ruler langsung mulai scrub — gabung klik & drag jadi satu,
  // jadi user gak perlu presisi grab lingkaran kecil buat mulai drag
  const handleScrubStart = useCallback(
    (e) => {
      e.preventDefault();
      setIsDraggingPlayhead(true);
      if (isPlaying) onPlayPause(); // pause dulu biar gak rebutan sama playback loop

      onSeek(timeFromClientX(e.clientX));

      const onMouseMove = (moveEvent) => {
        onSeek(timeFromClientX(moveEvent.clientX));
      };
      const onMouseUp = () => {
        setIsDraggingPlayhead(false);
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      };
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [isPlaying, onPlayPause, onSeek, timeFromClientX]
  );

  return (
    <div className="h-64 shrink-0 bg-[#0F0F0F] border-t border-[#145A43]/40 flex flex-col overflow-hidden">
      {/* Controls */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-[#145A43]/40">
        <button
          onClick={onToggleLoop}
          title="Loop preview"
          className={`text-xs px-2 py-1 rounded ${
            loop ? "bg-[#2F8F6F] text-[#0F0F0F]" : "bg-[#145A43] text-[#D7C7A1]"
          }`}
        >
          ⟲ Loop
        </button>
        <button
          onClick={onPlayPause}
          className="text-xs px-3 py-1 rounded bg-[#145A43] text-[#D7C7A1] hover:bg-[#2F8F6F] transition-colors"
        >
          {isPlaying ? "❚❚ Pause" : "▶ Play"}
        </button>
        <span className="text-xs text-[#D7C7A1]/70 tabular-nums">
          {currentTime.toFixed(2)}s
        </span>
        <div className="flex items-center gap-1.5 ml-auto">
          <span className="text-xs text-[#D7C7A1]/50">Duration</span>
          <input
            type="number"
            min={0.5}
            step={0.5}
            value={duration}
            onChange={(e) => onDurationChange(parseFloat(e.target.value) || 1)}
            className="w-16 bg-[#0F0F0F] border border-[#145A43] rounded px-2 py-0.5 text-xs text-[#D7C7A1] focus:outline-none focus:border-[#2F8F6F]"
          />
          <span className="text-xs text-[#D7C7A1]/50">s</span>
        </div>
      </div>

      {/* Ruler + playhead */}
      <div className="flex items-stretch border-b border-[#145A43]/40 relative">
        <div className="w-32 shrink-0 bg-[#0F0F0F] sticky left-0 z-10" />
        <div
          ref={rulerRef}
          onMouseDown={handleScrubStart}
          className="relative flex-1 h-6 cursor-pointer select-none"
          style={{ width: `${width}px` }}
        >
          {Array.from({ length: Math.ceil(duration) + 1 }).map((_, s) => (
            <div
              key={s}
              className="absolute top-0 h-full border-l border-[#145A43]/50 text-[10px] text-[#D7C7A1]/40 pl-1"
              style={{ left: `${s * PX_PER_SEC}px` }}
            >
              {s}s
            </div>
          ))}
          <div
            className="absolute top-0 h-full w-px bg-[#D7C7A1] z-10 pointer-events-none"
            style={{ left: `${currentTime * PX_PER_SEC}px` }}
          >
            <div
              className={`w-3 h-3 bg-[#D7C7A1] rounded-full -translate-x-1/2 -translate-y-1.5 transition-transform ${
                isDraggingPlayhead ? "scale-125" : ""
              }`}
            />
          </div>
        </div>
      </div>

      {/* Tracks */}
      <div className="flex-1 overflow-auto">
        {!selectedId && (
          <p className="px-4 py-6 text-xs text-[#D7C7A1]/40">
            Pilih elemen di canvas, lalu tambahkan keyframe lewat panel Properties.
          </p>
        )}
        {selectedId && activeProps.length === 0 && (
          <p className="px-4 py-6 text-xs text-[#D7C7A1]/40">
            "{selectedLabel}" belum punya keyframe. Atur nilai di panel Properties lalu klik tombol "+ Keyframe".
          </p>
        )}
        {activeProps.map((prop) => (
          <TrackRow
            key={prop}
            label={PROP_LABELS[prop]}
            keyframes={propsForSelected[prop]}
            duration={duration}
            onMoveKeyframe={(oldTime, newTime) =>
              onMoveKeyframe(selectedId, prop, oldTime, newTime)
            }
            onDeleteKeyframe={(time) => onDeleteKeyframe(selectedId, prop, time)}
            onEasingChange={(time, easing) =>
              onEasingChange(selectedId, prop, time, easing)
            }
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          />
        ))}
      </div>
    </div>
  );
}
